import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiPackageVariantClosed, mdiPrinter, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import {
    useAssetLabels,
    useRetireLabel,
    openLabelSheet,
    openLabelRoll,
    type AssetLabel,
    type AssetKind,
} from '../../hooks/useLabels'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { LabelQRPreview } from './LabelQRPreview'
import { EmptyState } from '../ui/empty-state'
import { mdiQrcode } from '@mdi/js'

/**
 * AssetLabelsCard shows the QR sticker this asset carries and drives what a
 * manager does with it: print it on office stock, or export the whole building
 * as a production file for a converter.
 *
 * There is no "create" button. Every asset is minted a label at birth
 * (migration 156), so the question is no longer whether a code exists but
 * whether it has been printed and applied — which is what the list shows.
 * Retiring is still available, and creates room for a replacement.
 *
 * Retiring is deliberately an AlertDialog with an explanation rather than a
 * quiet delete: the code is never reused afterwards, which is a decision about
 * a physical object still glued to a wall somewhere.
 */
export function AssetLabelsCard({
    org,
    assetUid,
    // Suppressed when the card is rendered inside the header dialog, which
    // already carries the title — otherwise it appears twice.
    showHeading = true,
}: {
    org: string
    assetUid: string
    showHeading?: boolean
}) {
    const { t, i18n } = useTranslation('labels')
    const { data, isLoading } = useAssetLabels(org, assetUid)
    const [copies, setCopies] = useState(1)
    const [printing, setPrinting] = useState(false)
    const [rolling, setRolling] = useState(false)
    // What goes on the roll. Every asset carries a label now, so without a
    // filter the file would contain lots, floors and trade groupings — none of
    // which anybody sticks anything on.
    const [rollKinds, setRollKinds] = useState<AssetKind[]>(['equipment'])
    const [printError, setPrintError] = useState<string | null>(null)

    const labels = ((data as { data?: AssetLabel[] } | undefined)?.data ?? []).filter(l => l.status !== 'retired')

    // The production file: everything under this asset, one page per label, for
    // a converter to impose a roll from. Scoped to the SUBTREE, which is what
    // "the whole building" means — labels hang off the equipment, not off the
    // building.
    const printAll = async () => {
        setRolling(true)
        setPrintError(null)
        try {
            await openLabelRoll(org, {
                assetUid,
                kinds: rollKinds,
                lang: i18n.language.startsWith('fr') ? 'fr' : 'en',
            })
        } catch {
            setPrintError(t('print.roll_error'))
        } finally {
            setRolling(false)
        }
    }

    const print = async () => {
        setPrinting(true)
        setPrintError(null)
        try {
            await openLabelSheet(org, {
                assetUid,
                copies,
                lang: i18n.language.startsWith('fr') ? 'fr' : 'en',
            })
        } catch {
            setPrintError(t('print.error'))
        } finally {
            setPrinting(false)
        }
    }

    return (
        <div className="space-y-4" data-testid="asset-labels-card">
            <div className="flex items-start justify-between gap-2">
                {showHeading ? (
                    <div>
                        <h2 className="text-lg font-semibold text-foreground">{t('card.title')}</h2>
                        <p className="text-sm text-muted-foreground">{t('card.description')}</p>
                    </div>
                ) : (
                    <div />
                )}
                <div className="flex shrink-0 items-center gap-2">
                    {/* Copies of the ONE code this equipment has. Spares get
                        lost and a machine you can walk around wants the same
                        code on two faces — neither is a reason for a second
                        label in the registry. */}
                    <div className="flex items-center gap-1.5">
                        <Label htmlFor="label-copies" className="text-xs text-muted-foreground">
                            {t('card.copies')}
                        </Label>
                        <Input
                            id="label-copies"
                            type="number"
                            min={1}
                            max={100}
                            value={copies}
                            onChange={event => setCopies(Math.max(1, Number(event.target.value) || 1))}
                            className="h-8 w-16"
                            disabled={labels.length === 0}
                            data-testid="asset-labels-copies"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        aria-label={t('card.print')}
                        title={t('card.print')}
                        disabled={labels.length === 0 || printing}
                        onClick={print}
                        data-testid="asset-labels-print-btn"
                    >
                        <AppIcon path={mdiPrinter} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('card.print')}</span>
                    </Button>

                    {/* What goes on the production roll. Every asset carries a
                        label now, so without this the file would contain lots,
                        floors and trade groupings — none of which anybody
                        sticks anything on. */}
                    <Select
                        value={rollKinds[0] ?? 'equipment'}
                        onValueChange={value => setRollKinds([value as AssetKind])}
                    >
                        <SelectTrigger className="h-8 w-36" data-testid="asset-labels-roll-kind">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {(['equipment', 'location'] as AssetKind[]).map(kind => (
                                <SelectItem key={kind} value={kind}>
                                    {t(`kind.${kind}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        size="sm"
                        aria-label={t('card.print_all')}
                        title={t('card.print_all_hint')}
                        disabled={rolling}
                        onClick={printAll}
                        data-testid="asset-labels-print-all-btn"
                    >
                        <AppIcon path={mdiPackageVariantClosed} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('card.print_all')}</span>
                    </Button>
                </div>
            </div>

            {printError && (
                <p className="text-sm text-destructive" data-testid="asset-labels-print-error">
                    {printError}
                </p>
            )}

            {isLoading ? (
                <p className="text-sm text-muted-foreground">…</p>
            ) : labels.length === 0 ? (
                <EmptyState
                    variant="inline"
                    icon={mdiQrcode}
                    title={t('card.empty')}
                    data-testid="asset-labels-empty"
                />
            ) : (
                <ul className="divide-y divide-border rounded-md border border-border">
                    {labels.map(label => (
                        <LabelRow key={label.uid} org={org} assetUid={assetUid} label={label} />
                    ))}
                </ul>
            )}

        </div>
    )
}

function LabelRow({ org, assetUid, label }: { org: string; assetUid: string; label: AssetLabel }) {
    const { t, i18n } = useTranslation('labels')
    const [confirmOpen, setConfirmOpen] = useState(false)
    const retire = useRetireLabel(org, assetUid)

    return (
        <li className="flex items-center gap-3 p-3" data-testid={`asset-label-row-${label.code}`}>
            {/* The real QR, not a stand-in icon: pointing a phone at the screen
                is the cheapest way to confirm a code scans before spending a
                sheet of label stock on it. */}
            <LabelQRPreview org={org} labelUid={label.uid} code={label.code} />
            <div className="min-w-0 flex-1">
                {/* The code is the sticker's public URL, and following it is how
                    a demo goes straight to what a resident sees after scanning
                    (#1351). Deliberately styled as text, not as a link: a row of
                    underlined blue codes would read as navigation chrome rather
                    than as the identifiers they are. */}
                <p className="font-mono text-sm font-medium text-foreground">
                    <Link
                        to="/q/$code"
                        params={{ code: label.code }}
                        className="text-inherit no-underline hover:underline"
                        title={t('card.qr_open', { code: label.code })}
                        data-testid={`asset-label-code-${label.code}`}
                    >
                        {label.code}
                    </Link>
                </p>
                <p className="truncate text-xs text-muted-foreground">
                    {t(`status.${label.status}`)}
                    {label.material ? ` · ${t(`material.${label.material}`)}` : ''}
                    {' · '}
                    {label.printed_at
                        ? `${t('card.printed_at')} ${new Date(label.printed_at).toLocaleDateString(i18n.language)}`
                        : t('card.never_printed')}
                </p>
                {label.location_hint && (
                    <p className="truncate text-xs text-muted-foreground">{label.location_hint}</p>
                )}
            </div>
            {/* Ghost, not solid destructive: this is a row action in a list, and
                a column of red blocks would outshout the labels themselves. The
                weight belongs on the confirmation, which explains that the code
                is never reused. */}
            <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive"
                aria-label={t('card.retire')}
                title={t('card.retire')}
                onClick={() => setConfirmOpen(true)}
                data-testid={`asset-label-retire-${label.code}`}
            >
                <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('card.retire_confirm_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('card.retire_confirm_body')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('card.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="asset-label-retire-confirm"
                            onClick={() =>
                                retire.mutate({ params: { path: { orgCode: org, labelUid: label.uid } } })
                            }
                        >
                            {t('card.retire_confirm_action')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </li>
    )
}
